import { connectorAssetsApi } from "../api";
import type { ConnectorAssetSampleResponsePayload } from "../api";
import {
  LIVE_SAMPLE_MAX_ROWS,
  mapLiveSampleColumns,
  mapLiveSampleRows,
} from "./dataStudioLiveSampleMappers";
import type {
  GetDataStudioTableColumnsResponse,
  GetDataStudioTableRowsResponse,
} from "./dataStudioTables.types";

export interface LiveSampleRequest {
  /** The connector type's catalog key, e.g. `google_sheets`. */
  slug: string;
  /** `connector_connections.id` of the connection. */
  connectionId: string;
  /** The connector asset (a sheet tab or SaaS object) to sample. */
  assetId: string;
  page?: number;
  limit?: number;
}

/** One page of the source's live sample; the React Query cache keeps it for both Columns and Preview. */
export const getLiveSample = ({
  slug,
  connectionId,
  assetId,
  page = 1,
  limit = LIVE_SAMPLE_MAX_ROWS,
}: LiveSampleRequest): Promise<ConnectorAssetSampleResponsePayload> =>
  connectorAssetsApi.sample(slug, assetId, {
    connection_id: connectionId,
    page,
    limit_rows: Math.min(limit, LIVE_SAMPLE_MAX_ROWS),
  });

export const getLiveSampleColumns = async (
  request: LiveSampleRequest,
): Promise<GetDataStudioTableColumnsResponse> =>
  mapLiveSampleColumns(await getLiveSample(request));

/** Rows for a row window; the offset must be a multiple of the (clamped) limit, as the grid's paging is. */
export const getLiveSampleRows = async (
  request: LiveSampleRequest & { offset?: number },
): Promise<GetDataStudioTableRowsResponse> => {
  const limit = Math.min(request.limit ?? LIVE_SAMPLE_MAX_ROWS, LIVE_SAMPLE_MAX_ROWS);
  const offset = request.offset ?? 0;
  const sample = await getLiveSample({ ...request, limit, page: Math.floor(offset / limit) + 1 });
  return mapLiveSampleRows(sample, offset, limit);
};
